/**
 * Searchable Select Dropdown
 *
 * A dropdown with a built-in search/filter input.
 * Used for large lists like industry selection, and for the contractor and
 * contractor-worker pickers.
 *
 * Options are `{ value, label }` and may also carry:
 *   hint   — a second line under the label
 *   badge  — a chip on the right
 * BOTH ARE SEARCHED along with the label. That is the point of them: on a
 * contractor worker list the term someone actually types is as often the trade
 * or the gate-pass number as the name.
 *
 * Keyboard: ArrowDown/ArrowUp move, Enter picks the highlighted row, Escape
 * closes without changing anything. Mouse hover and the keyboard share one
 * highlight index so they cannot disagree about what Enter will pick.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

/** Rows rendered before the list tells the user to narrow the search. */
const MAX_VISIBLE = 50;

const SearchableSelect = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  /** Placeholder for the SEARCH box inside the open panel. The trigger and the
   *  search box want different words — the trigger says what is selected (or
   *  "None"), the search box says what you can type. Defaults to `placeholder`
   *  so existing callers are unchanged. */
  searchPlaceholder,
  className = '',
  /** Disable the control entirely. */
  disabled = false,
  /** Used for the aria wiring; pass one when the field has a <label>. */
  id,
  /** Shown when the query matches nothing. */
  noResultsText = 'No matches found',
  /** ({ query, close }) => node — a persistent row at the foot of the open
   *  list, below the results. For an action that must stay reachable even when
   *  the query filters every option away: "add a new one" is needed precisely
   *  then. Receives the query so the action can pre-fill from what was typed. */
  renderFooter,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((opt) =>
      `${opt.label || ''} ${opt.hint || ''} ${opt.badge || ''}`
        .toLowerCase().includes(q));
  }, [options, search]);

  const visible = filtered.slice(0, MAX_VISIBLE);

  // Reset the highlight whenever the visible set changes, so Enter can never
  // commit a row that scrolled out from under the index.
  useEffect(() => { setHighlight(0); }, [search]);

  // Keep the highlighted row in view when moving by keyboard.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector('[data-highlighted="true"]');
    if (node) node.scrollIntoView({ block: 'nearest' });
  }, [highlight, open]);

  const selectedLabel = (options || []).find(o => o.value === value)?.label || '';

  const handleSelect = (val) => {
    onChange(val, (options || []).find(o => o.value === val) || null);
    setOpen(false);
    setSearch('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!visible.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight(h => (h + step + visible.length) % visible.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();          // never submit the surrounding form
      if (visible[highlight]) handleSelect(visible[highlight].value);
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setSearch('');
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
      >
        <span className={selectedLabel ? 'text-gray-900' : 'text-gray-500'}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-hidden">
          {/* Search input */}
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded={open}
                aria-autocomplete="list"
                aria-controls={id ? `${id}-listbox` : undefined}
                aria-activedescendant={id && visible[highlight]
                  ? `${id}-opt-${highlight}` : undefined}
                autoComplete="off"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={searchPlaceholder || placeholder}
                className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div ref={listRef} id={id ? `${id}-listbox` : undefined} role="listbox"
               className="max-h-56 overflow-y-auto">
            {visible.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">{noResultsText}</div>
            ) : (
              visible.map((opt, i) => (
                <button
                  key={opt.value}
                  id={id ? `${id}-opt-${i}` : undefined}
                  type="button"
                  role="option"
                  aria-selected={opt.value === value}
                  data-highlighted={i === highlight}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => handleSelect(opt.value)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between gap-2 ${
                    i === highlight ? 'bg-blue-50' : ''
                  } ${opt.value === value ? 'text-blue-700 font-medium' : 'text-gray-700'}`}
                >
                  <span className="min-w-0">
                    <span className="block truncate">{opt.label}</span>
                    {opt.hint && (
                      <span className="block truncate text-xs text-gray-500">{opt.hint}</span>
                    )}
                  </span>
                  {opt.badge && (
                    <span className="shrink-0 rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {opt.badge}
                    </span>
                  )}
                </button>
              ))
            )}
            {filtered.length > MAX_VISIBLE && (
              <div className="bg-gray-50 px-3 py-2 text-center text-xs text-gray-500">
                Showing first {MAX_VISIBLE} of {filtered.length}. Type to refine.
              </div>
            )}
          </div>

          {/* Clear option */}
          {value && (
            <div className="border-t border-gray-100 p-1">
              <button
                type="button"
                onClick={() => handleSelect('')}
                className="w-full text-left px-3 py-1.5 text-xs text-gray-500 hover:bg-gray-50 rounded"
              >
                Clear selection
              </button>
            </div>
          )}

          {renderFooter && (
            <div className="border-t border-gray-100 bg-gray-50">
              {renderFooter({ query: search, close: () => { setOpen(false); setSearch(''); } })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
